import React, { createContext, useContext } from 'react';
import * as courseworkApi from '../api/coursework';
import { useAuth } from './AuthContext';
import { useTopics } from './TopicsContext';

// The coursework store: the student's assignments and quizzes, with their own
// submissions already folded in.
//
// It used to also hold the teacher's marking list, mirroring admin-created items
// into the student list by hand so the two views couldn't drift. That job now
// belongs to the server — both sides read the same tables — so this provider
// only serves the student pages, and AdminSubmissions talks to the admin
// endpoints directly.
const CourseworkContext = createContext(null);

export function CourseworkProvider({ children }) {
  const { user, restoring } = useAuth();
  /**
   * Only for the subject join below — see `bySubject`. TopicsProvider is mounted
   * outside this one (App.js), so the list is normally available; read
   * defensively so this provider still stands alone without it, and the join
   * simply doesn't happen.
   */
  const topicsCtx = useTopics();
  const topics = React.useMemo(() => topicsCtx?.topics || [], [topicsCtx]);
  const [rawHomework, setHomework] = React.useState([]);
  const [rawQuizzes, setQuizzes] = React.useState([]);
  const [submissions, setSubmissions] = React.useState([]);
  const [totals, setTotals] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Keyed on the id, not the user object — see the same note in TopicsContext:
  // depending on the object re-runs this on every render.
  const userId = user ? user.id : null;
  const isStudent = user ? user.role === 'student' : false;

  const load = React.useCallback(async () => {
    // These are student endpoints; an admin token gets 401s from them.
    if (!userId || !isStudent) {
      setHomework([]); setQuizzes([]); setSubmissions([]); setTotals(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await courseworkApi.loadStudentCoursework();
      setHomework(data.homework);
      setQuizzes(data.quizzes);
      setSubmissions(data.submissions);
      setTotals(data.totals);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [userId, isStudent]);

  React.useEffect(() => {
    if (restoring) return;
    load();
  }, [load, restoring]);

  /**
   * Hands in a PDF that has already been uploaded to R2, then refetches so the
   * card's state comes from the server rather than from a guess. A re-submit is
   * the same call — the backend replaces the previous answer.
   */
  const submitHomework = async (id, { file }) => {
    await courseworkApi.submitAssignment(id, file);
    await load();
  };

  const submitQuizAnswers = async (id, { file }) => {
    await courseworkApi.submitQuiz(id, file);
    await load();
  };

  /**
   * Fills in each item's subject from its topic.
   *
   * Neither getAllAssignments nor getAllQuizzes returns a `subject` — it belongs
   * to the topic server-side — so without this join every card fell back to the
   * adapter's default and the whole coursework page was tinted Biology.
   */
  const bySubject = React.useCallback((items) => items.map((i) => {
    const topic = topics.find((t) => t.id === i.topicId);
    return topic ? { ...i, subj: topic.subj } : i;
  }), [topics]);

  const homework = React.useMemo(() => bySubject(rawHomework), [rawHomework, bySubject]);
  const quizzes = React.useMemo(() => bySubject(rawQuizzes), [rawQuizzes, bySubject]);

  const value = React.useMemo(() => ({
    homework,
    quizzes,
    submissions,
    totals,
    loading,
    error,
    reload: load,
    upcomingQuizzes: quizzes.filter((q) => q.status !== 'completed'),
    submitHomework,
    submitQuizAnswers,
  }), [homework, quizzes, submissions, totals, loading, error, load]); // eslint-disable-line react-hooks/exhaustive-deps

  return <CourseworkContext.Provider value={value}>{children}</CourseworkContext.Provider>;
}

export const useCoursework = () => useContext(CourseworkContext);
